import { IStayDuration } from '@app/interfaces/organization-passport/stay-duration'
import FormList from '@app/pages/reference-book-page-details/components/form-list/form-list'
import ModalFooter from '@app/shared/modal-footer/modal-footer'
import { openNotificationWithIcon } from '@app/shared/notification/notification'
import {
  useCreateStayDurationMutation,
  useUpdateStayDurationMutation,
} from '@app/store/rtk/api/organization-passport/stay-duration-api'
import { Button, Form, Input, InputNumber, Modal, Spin, Switch } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface props {
  stayDuration?: IStayDuration
  edit?: boolean
}

const StayDurationForm: React.FC<props> = ({ stayDuration, edit }) => {
  const [visible, setVisible] = useState<boolean>(false)
  const [form] = Form.useForm()
  const [t] = useTranslation()
  const [create, { isLoading: createLoading, isSuccess: isCreateSuccess }] =
    useCreateStayDurationMutation()
  const [update, { isLoading: updateLoading, isSuccess: isUpdateSuccess }] =
    useUpdateStayDurationMutation()
  const confirmLoading = useMemo(
    () => createLoading || updateLoading,
    [createLoading, update],
  )
  const success = useMemo(
    () => isCreateSuccess || isUpdateSuccess,
    [isCreateSuccess, isUpdateSuccess],
  )

  const showModal = () => setVisible(true)
  const hideModal = () => {
    setVisible(false)
    form.resetFields()
  }

  const onFinish = async (values: any) => {
    const languages = values.data.filter(
      (language) => language && language.name,
    )
    const newValues: IStayDuration = {
      id: values.id,
      sorting: values.sorting,
      status: values.status,
      data: languages,
    }
    const stayDurationSubmitApi = stayDuration ? update : create
    stayDurationSubmitApi(newValues)
  }

  useEffect(() => {
    if (success) {
      openNotificationWithIcon('success', t('success.updated'))
      form.resetFields()
      hideModal()
    }
  }, [success])

  return (
    <React.Fragment>
      <Button onClick={showModal} type={'primary'}>
        {edit ? (
          <i className="far fa-edit"> {t('general.edit')} </i>
        ) : (
          t('general.add')
        )}
      </Button>

      <Modal
        open={visible}
        title={
          edit
            ? t('childrenStayDuration.editStayDuration')
            : t('childrenStayDuration.addStayDuration')
        }
        confirmLoading={confirmLoading}
        onCancel={hideModal}
        footer={[
          <React.Fragment key={1}>
            <ModalFooter
              confirm={confirmLoading}
              edit={edit}
              onCancel={hideModal}
              onSubmit={form.submit}
            />
          </React.Fragment>,
        ]}
      >
        <Spin spinning={confirmLoading}>
          <Form
            form={form}
            onFinish={onFinish}
            initialValues={{
              id: stayDuration?.id,
              sorting: stayDuration?.sorting || 0,
              status: edit ? stayDuration?.status : true,
            }}
            layout={'vertical'}
          >
            <Form.Item hidden={true} name={'id'}>
              <Input />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('errors.requiredMessage'),
                },
              ]}
              name={'status'}
              label={t('general.status')}
              valuePropName="checked"
            >
              <Switch
                disabled={confirmLoading}
                defaultChecked={stayDuration?.status === true}
              />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  type: 'number',
                  min: -2147483648,
                  max: 2147483648,
                  message: t('errors.minPriorityMessage'),
                },
              ]}
              label={t('general.sort')}
              name={'sorting'}
            >
              <InputNumber
                disabled={confirmLoading}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <FormList confirmLoading={confirmLoading} object={stayDuration} />
          </Form>
        </Spin>
      </Modal>
    </React.Fragment>
  )
}

export default StayDurationForm
